import { ReduceStore } from "flux/utils";
import UserActions from "data/UserActions";
import userStore from "data/userStore";
import { fromSlice } from "data/slices";
import EntityName from "data/util/EntityName";

/**
 * I wrap a named preference which stores a field set definition, and allow
 * performant querying of whether fields are included in it, across all
 * implementations the user has access to for their active organization.
 */
class FieldSetPreferenceCache extends ReduceStore {

    constructor(dispatcher, prefName) {
        super(dispatcher);
        this.prefName = prefName;
    }

    getInitialState() {
        return {}; // Object<implKey, Map<entityKey, Set<fieldKey>>>
    }

    _updateFieldSet(state, implKey, value) {
        try {
            const map = new Map();
            if (value) {
                const parsed = JSON.parse(value);
                for (const ek of Object.keys(parsed)) {
                    map.set(ek, new Set(parsed[ek]));
                }
            }
            return {
                ...state,
                [implKey]: map,
            };
        } catch (e) {
            console.warn(`Error processing ${this.prefName} for ${implKey}`, e);
            return state;
        }
    }

    reduce(state, action) {
        switch (action.type) {
            case UserActions.LOADED: {
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                ]);
                return fromSlice(userStore.getActiveOrg().lics)
                    .reduce((s, l) =>
                        this._updateFieldSet(s, l.key, l.prefs && l.prefs[this.prefName]), state);
            }

            case UserActions.LICENSE_PREFERENCE_UPDATED: {
                if (action.pref !== this.prefName) return state;
                return this._updateFieldSet(state, action.impl, action.newValue);
            }

            default:
                return state;
        }
    }

    /**
     * I indicate whether the specified field is included by the preference's
     * fieldset. The `entity` parameter may be qualified, but doesn't have to
     * be. Thus, to check `cath/5/patient.firstname`, all of these argument
     * tuples will behave the same:
     *
     *  * `isIncluded("cath", "patient", "firstname")`
     *  * `isIncluded("cath", "cath/5/patient", "firstname")`
     *  * `isIncluded("cath/5/patient", "firstname")`
     *
     * In the second variant, the first segment of the three-part entity key is
     * ignored. It _should_ be the same as the passed implementation key, but
     * will not be validated as such. In both the second and third variants, the
     * version key is ignored, as fieldset preferences span all versions of the
     * implementation.
     */
    isIncluded(impl, entity, field) {
        if (arguments.length === 2) {
            // variant three
            field = entity;
            const en = new EntityName(impl);
            impl = en.implKey;
            entity = en.bareEntityKey;
        } else if (entity.indexOf("/") > 0) {
            // variant two
            const en = new EntityName(entity);
            if (process.env.NODE_ENV !== "production" && en.implKey !== impl) {
                console.warn("isIncluded was passed a three-part entity key (%s) inconsistent w/ the passed impl key (%s).", entity, impl);
            }
            entity = en.bareEntityKey;
        }
        const s = this.getState();
        if (!s.hasOwnProperty(impl)) return false;
        const es = s[impl];
        if (!es.has(entity)) return false;
        return es.get(entity).has(field);
    }

}

export default FieldSetPreferenceCache;



// WEBPACK FOOTER //
// ./app/data/util/FieldSetPreferenceCache.js